import {
  PickerPageSchema,
  adminClient,
  apiClient,
  parseResp,
} from "@shared/api";
import type { PickerItem, PickerPage } from "@shared/lib";
import type { PickerSource } from "@shared/ui";
import {
  AdminOrgMemberPageSchema,
  AdminOrgSchema,
  AdminUserSchema,
  ProjectAccessCandidateListSchema,
} from "./schemas";

// Источники пикеров (единый контракт, раздел «Пикеры»): куда уходит поиск и
// как по id найти имя для фильтра, открытого по ссылке. Пикер отдаёт только
// то, что вызывающему можно видеть; чужое сервер не находит (404).

const quiet = { silentError: true } as const;
const enc = encodeURIComponent;

const adminPicker =
  (path: string, extra: Record<string, string> = {}) =>
  (q: string, { limit, signal }: { limit: number; signal: AbortSignal }) =>
    adminClient
      .get(path, {
        ...quiet,
        signal,
        params: { ...(q ? { q } : {}), limit, ...extra },
      })
      .then(parseResp(PickerPageSchema));

const resolveUser = (id: string, signal: AbortSignal) =>
  adminClient
    .get(`/users/${enc(id)}`, { ...quiet, signal })
    .then(parseResp(AdminUserSchema))
    .then((u): PickerItem => ({
      kind: "user",
      id: u.id,
      name: u.name,
      detail: u.email,
    }));

const resolveOrg = (slug: string, signal: AbortSignal) =>
  adminClient
    .get(`/orgs/${enc(slug)}`, { ...quiet, signal })
    .then(parseResp(AdminOrgSchema))
    .then((o): PickerItem => ({
      kind: "org",
      id: o.slug,
      name: o.name,
      detail: o.slug,
    }));

/** Одна запись пикера по id (`?id=`): имя для фильтра, открытого по ссылке. */
const resolveById =
  (path: string) =>
  (id: string, signal: AbortSignal): Promise<PickerItem | null> =>
    adminClient
      .get(path, { ...quiet, signal, params: { id } })
      .then(parseResp(PickerPageSchema))
      .then((page) => page.items[0] ?? null);

export type UserPickerFilters = {
  /** Только активные аккаунты: для добавления в организацию и передачи. */
  status?: "active";
  /** Исключить участников организации (slug). */
  notInOrg?: string;
  /** Исключить участников группы (id). */
  notInGroup?: string;
};

/** Пикеры админки установки: видят всех. */
export const adminPickers = {
  users: (filters: UserPickerFilters = {}): PickerSource => {
    const extra: Record<string, string> = {};
    if (filters.status) extra.status = filters.status;
    if (filters.notInOrg) extra.not_in_org = filters.notInOrg;
    if (filters.notInGroup) extra.not_in_group = filters.notInGroup;
    return {
      key: ["admin", "pickers", "users", extra],
      search: adminPicker("/pickers/users", extra),
      resolve: resolveUser,
    };
  },
  orgs: (): PickerSource => ({
    key: ["admin", "pickers", "orgs"],
    search: adminPicker("/pickers/orgs"),
    resolve: resolveOrg,
  }),
  projects: (): PickerSource => ({
    key: ["admin", "pickers", "projects"],
    search: adminPicker("/pickers/projects"),
    resolve: resolveById("/pickers/projects"),
  }),
  runners: (): PickerSource => ({
    key: ["admin", "pickers", "runners"],
    search: adminPicker("/pickers/runners"),
    resolve: resolveById("/pickers/runners"),
  }),
  /**
   * Участники организации: из них собирают группы. `notInGroup` убирает
   * тех, кто в группе уже есть, на сервере.
   */
  orgMembers: (slug: string, notInGroup?: string): PickerSource => ({
    key: ["admin", "pickers", "org-members", slug, notInGroup ?? null],
    search: (q, { limit, signal }) =>
      adminClient
        .get(`/orgs/${enc(slug)}/members`, {
          ...quiet,
          signal,
          params: {
            ...(q ? { q } : {}),
            limit,
            offset: 0,
            sort: "name",
            ...(notInGroup ? { not_in_group: notInGroup } : {}),
          },
        })
        .then(parseResp(AdminOrgMemberPageSchema))
        .then((page): PickerPage => ({
          items: page.items.map((m) => ({
            kind: "user",
            id: m.user_id,
            name: m.name,
            detail: m.email,
          })),
          has_more: page.total > page.items.length,
        })),
    resolve: resolveUser,
  }),
};

/** Кандидаты в участники проекта: люди и группы общих организаций. */
export const projectCandidatePicker = (
  projectId: string,
  kind?: "user" | "group",
): PickerSource => ({
  key: ["project", projectId, "candidates", kind ?? "all"],
  search: (q, { limit, signal }) =>
    apiClient
      .get(`/projects/${enc(projectId)}/members/candidates`, {
        ...quiet,
        signal,
        params: { ...(q ? { q } : {}), limit, ...(kind ? { kind } : {}) },
      })
      .then(parseResp(ProjectAccessCandidateListSchema))
      .then((page): PickerPage => ({
        items: page.items.map((c) => ({
          kind: c.subject_kind,
          id: c.subject_id,
          name: c.name,
          detail: c.detail,
        })),
        has_more: page.has_more,
      })),
});

/** Кому передать проект: люди общих организаций и свои организации (owner, admin). */
export const projectTransferPicker = (
  projectId: string,
  kind: "user" | "org",
): PickerSource => ({
  key: ["project", projectId, "transfer-candidates", kind],
  search: (q, { limit, signal }) =>
    apiClient
      .get(`/projects/${enc(projectId)}/transfer-candidates`, {
        ...quiet,
        signal,
        params: { ...(q ? { q } : {}), limit, kind },
      })
      .then(parseResp(PickerPageSchema)),
});
